// Pure helpers behind the Provider Detail panel's "Mention by category" and
// "Citations used" sections.
//
// Both Supabase RPCs return a one-element array wrapping an aggregate object, while the
// panel was written against the flat row arrays the Express query returned. A
// one-element array is not empty, so the panel's `length === 0` guard never
// fired — it mapped over the wrapper and rendered a single row of `undefined`
// fields instead of showing an empty state. See
// .local/docs/RI_AIM_Develop_vs_Staging_Parity_2026-07-29.md.

/** Unwrap the Supabase RPC's `[{...}]` envelope, tolerating a bare object. */
const unwrap = (payload) => {
  if (Array.isArray(payload)) return payload.length ? payload[0] : null;
  return payload && typeof payload === 'object' ? payload : null;
};

const toNumberOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Normalize a provider-categories payload into the rows the section renders.
 *
 * Two shapes have shipped:
 *   - Express/MySQL query — `[{ category, category_display, mention_pct }, …]`
 *   - Supabase RPC        — `[{ total, provider, categories: [{ category, count }, …] }]`
 *
 * The Supabase RPC moved the rows one level down and replaced the per-category rate with
 * a raw `count`. Its counts sum exactly to `total`, so what it describes is a
 * **distribution of mentions across categories**, not a per-category mention
 * rate — the two are not interchangeable, so both are surfaced separately:
 *
 *   - `mention_pct` — a genuine 0–100 rate, only when the payload supplies one.
 *     `null` under the Supabase RPC shape, because it cannot be derived from counts.
 *   - `share_pct`   — `count / total × 100`, the share of this provider's
 *     mentions that fell in this category. `null` when counts are unavailable.
 *
 * Callers pick the bar to draw from which one is present: a quality-coloured
 * rating bar is only meaningful for `mention_pct`.
 *
 * @param {Array|object|null|undefined} payload the endpoint payload
 * @returns {Array<object>} `{ category, category_display, count, mention_pct, share_pct }`
 */
export function providerCategoryRows(payload) {
  // Legacy flat shape: an array whose entries are already category rows.
  if (Array.isArray(payload) && payload.some((r) => r && typeof r === 'object' && 'category' in r)) {
    return payload
      .filter((r) => r && typeof r === 'object')
      .map((r) => ({
        ...r,
        count: toNumberOrNull(r.count),
        mention_pct: toNumberOrNull(r.mention_pct),
        share_pct: toNumberOrNull(r.share_pct),
      }));
  }

  const wrapper = unwrap(payload);
  const rows = Array.isArray(wrapper?.categories) ? wrapper.categories : [];
  if (!rows.length) return [];

  const total = toNumberOrNull(wrapper.total)
    ?? rows.reduce((sum, r) => sum + (toNumberOrNull(r?.count) ?? 0), 0);

  return rows
    .filter((r) => r && typeof r === 'object')
    .map((r) => {
      const count = toNumberOrNull(r.count);
      const mentionPct = toNumberOrNull(r.mention_pct);
      return {
        ...r,
        count,
        mention_pct: mentionPct,
        share_pct: mentionPct ?? (count != null && total ? (count / total) * 100 : null),
      };
    });
}

/**
 * Normalize a provider-citations payload into the rows the section renders.
 *
 * Two shapes have shipped:
 *   - Express/MySQL query — `[{ cited_domain, citations, is_client_site }, …]`
 *   - Supabase RPC        — `[{ total_citations, distinct_domains,
 *                              domains: [{ domain, count, is_client_site }, …] }]`
 *
 * The Supabase RPC moved the rows one level down and renamed both fields, so the panel's
 * `c.cited_domain` / `c.citations` reads produced `undefined` on every row.
 *
 * @param {Array|object|null|undefined} payload the endpoint payload
 * @returns {Array<object>} `{ cited_domain, citations, is_client_site }`
 */
export function providerCitationRows(payload) {
  // Legacy flat shape: entries are already citation rows.
  if (Array.isArray(payload) && payload.some((r) => r && typeof r === 'object' && 'cited_domain' in r)) {
    return payload
      .filter((r) => r && typeof r === 'object')
      .map((r) => ({
        ...r,
        cited_domain: r.cited_domain,
        citations: toNumberOrNull(r.citations),
        is_client_site: Boolean(r.is_client_site),
      }));
  }

  const wrapper = unwrap(payload);
  const rows = Array.isArray(wrapper?.domains) ? wrapper.domains : [];
  return rows
    .filter((r) => r && typeof r === 'object')
    .map((r) => ({
      ...r,
      cited_domain: r.cited_domain ?? r.domain ?? '',
      citations: toNumberOrNull(r.citations ?? r.count),
      is_client_site: Boolean(r.is_client_site),
    }));
}

/**
 * Normalize a provider-facts payload for the "Fact statements" section.
 *
 * `fn_get_aim_provider_facts` returns one row per **statement**, not per fact —
 * it is keyed on `response_id`, so the same fact recurs once per response that
 * mentioned it. The 2026-08-10 capture is 315 rows over 49 distinct `fact_key`s
 * across 24 responses; `has_heated_pool` alone appears 23 times.
 *
 * Rows are ordered worst-first — hallucinations, then inaccurate, then unscored,
 * then accurate — so the handful worth acting on sit at the top instead of being
 * buried behind a few hundred correct restatements. Order within each band is
 * the payload's own.
 *
 * Only rows that name a fact are kept. A per-provider aggregate — the shape the
 * RPC returned while RPC-10 was broken — names none, and rendering it as a fact
 * list produced a single row of `undefined` fields; it now yields the empty
 * state, which the panel header's own accuracy rate already contextualises.
 *
 * @param {Array|object|null|undefined} payload the endpoint payload
 * @returns {{mode: 'facts'|'empty', facts: Array<object>}}
 */
export function providerFactsView(payload) {
  const facts = (Array.isArray(payload) ? payload : [])
    .filter((r) => r && typeof r === 'object' && ('fact_key' in r || 'fact_value_stated' in r));
  if (!facts.length) return { mode: 'empty', facts: [] };
  const rank = (r) => {
    if (r.is_hallucination) return 0;
    if (r.is_accurate === false) return 1;
    if (r.is_accurate == null) return 2;
    return 3;
  };
  return {
    mode: 'facts',
    facts: facts
      .map((r, i) => ({ r, i }))
      .sort((a, b) => rank(a.r) - rank(b.r) || a.i - b.i)
      .map(({ r }) => r),
  };
}
